import axios from 'axios';
const adminURL = process.env.VUE_APP_ADMIN_URL;

/**
 * Second-factor (TOTP) flow.
 *
 * The enrollment token lives under its own `mfaToken` key, never under
 * `accessToken`. Home.vue treats `accessToken` as a real session, so storing the
 * enrollment token there would let a half-authenticated user reach the shell.
 */
export default class MfaService {

  static getMfaToken() {
    const raw = localStorage.getItem('mfaToken');
    return raw ? JSON.parse(raw) : null;
  }

  static clearMfaToken() {
    localStorage.removeItem('mfaToken');
  }

  static authHeader() {
    return { headers: { 'Authorization': `Bearer ${MfaService.getMfaToken()}` } };
  }

  /** Requests a QR code to register an authenticator app. */
  static async setup() {
    try {
      const response = await axios.post(`${adminURL}/useradmin/mfa/setup`, {}, MfaService.authHeader());
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      }
      return { status: 'error' };
    } catch (error) {
      return MfaService.toError(error);
    }
  }

  /**
   * Submits a 6-digit code or a recovery code.
   *
   * Two possible successful outcomes:
   * - A TOTP code returns an operational token, which replaces the enrollment token.
   * - A recovery code returns a fresh enrollment token and `nextStep: 'mfa_setup'`:
   *   the backend destroyed the old registration (the device is gone), so the
   *   administrator must register a replacement before getting a session.
   */
  static async verify(code) {
    try {
      const response = await axios.post(`${adminURL}/useradmin/mfa/verify`, { code }, MfaService.authHeader());
      const data = response.data.data ?? {};

      if (response.data.status !== 'ok') {
        return { status: 'error' };
      }

      if (data.nextStep === 'mfa_setup' && data.mfaToken) {
        localStorage.setItem('mfaToken', JSON.stringify(data.mfaToken));
        return { status: 'ok', nextStep: 'mfa_setup' };
      }

      if (data.token) {
        localStorage.setItem('accessToken', JSON.stringify(data.token));
        MfaService.clearMfaToken();
        return { status: 'ok', data };
      }

      return { status: 'error' };
    } catch (error) {
      return MfaService.toError(error);
    }
  }

  /** Clears another administrator's second factor. Requires a verified session. */
  static async reset(id) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.post(`${adminURL}/useradmin/${id}/mfa/reset`, {}, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      return response.data.status === 'ok' ? { status: 'ok' } : { status: 'error' };
    } catch (error) {
      return MfaService.toError(error);
    }
  }

  /**
   * Normalises an axios failure into the machine-readable code the views branch
   * on, so no view has to parse a Spanish message.
   */
  static toError(error) {
    const data = error?.response?.data ?? {};
    return {
      status: 'error',
      code: data.code ?? null,
      message: data.message ?? 'Ocurrió un error, intente nuevamente'
    };
  }

}
